import React, { useState, useEffect, useCallback } from "react";
import { auleloApi } from "@/lib/auleloApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Save, Loader2, Tag as TagIcon, Trash2, FileText, Sparkles, AlertTriangle, BookOpen, Wand2 } from "lucide-react";
import { toast } from "sonner";
import RichEditor from "@/components/RichEditor";

/** NotesPanel — list + editor for Vasa notes, optionally scoped to a tour. */
export default function NotesPanel({ tourId = null, scopeLabel = "All notes" }) {
  const [notes, setNotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filterTag, setFilterTag] = useState("");
  const [filterQ, setFilterQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [related, setRelated] = useState([]);
  const [contradictions, setContradictions] = useState([]);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [proactiveBusy, setProactiveBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (tourId) params.set("tour_id", tourId);
      if (filterTag) params.set("tag", filterTag);
      if (filterQ.trim()) params.set("q", filterQ.trim());
      const { data } = await auleloApi.get(`/vasa/notes?${params.toString()}`);
      setNotes(data || []);
    } catch (e) {
      toast.error("Failed to load notes");
    } finally {
      setBusy(false);
    }
  }, [tourId, filterTag, filterQ]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing({ title: "", body_html: "", tags: tourId ? ["tour"] : [] }); setRelated([]); setContradictions([]); setSuggestedTags([]); };
  const startEdit = (n) => { setEditing({ ...n }); setRelated([]); setContradictions([]); setSuggestedTags([]); };

  // Proactive Vasa: when the editor opens, find related past notes.
  useEffect(() => {
    if (!editing) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await auleloApi.post("/vasa/proactive/related", {
          title: editing.title || "",
          body_html: editing.body_html || "",
          tour_id: tourId,
          note_id: editing.id,
        });
        setRelated(data.related || []);
      } catch {}
    }, 350);
    return () => clearTimeout(t);
  }, [editing?.id, tourId, editing?.title]);

  const runAutoTag = async () => {
    if (!editing) return;
    setProactiveBusy(true);
    try {
      const { data } = await auleloApi.post("/vasa/proactive/autotag", { title: editing.title, body_html: editing.body_html });
      setSuggestedTags(data.tags || []);
      if (!(data.tags || []).length) toast.message("No tag suggestions yet — add more content.");
    } catch (e) { toast.error("Auto-tag failed"); }
    finally { setProactiveBusy(false); }
  };

  const runContradictionCheck = async () => {
    if (!editing) return;
    setProactiveBusy(true);
    try {
      const { data } = await auleloApi.post("/vasa/proactive/contradictions", {
        title: editing.title, body_html: editing.body_html, tour_id: tourId, note_id: editing.id,
      });
      setContradictions(data.contradictions || []);
      if (!(data.contradictions || []).length) toast.success("No contradictions detected.");
      else toast.message(`${data.contradictions.length} potential contradiction(s) found`);
    } catch (e) { toast.error("Contradiction check failed"); }
    finally { setProactiveBusy(false); }
  };

  const save = async () => {
    if (!editing.title.trim() || !editing.body_html?.replace(/<[^>]+>/g, "").trim()) {
      toast.error("Title and body required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: editing.title.trim(),
        body_html: editing.body_html,
        tags: editing.tags || [],
        tour_id: tourId,
      };
      if (editing.id) {
        await auleloApi.patch(`/vasa/notes/${editing.id}`, payload);
        toast.success("Saved");
      } else {
        await auleloApi.post("/vasa/notes", payload);
        toast.success("Note added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await auleloApi.delete(`/vasa/notes/${id}`);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const addTag = (t) => {
    const tag = (t || "").trim().toLowerCase();
    if (!tag) return;
    if ((editing.tags || []).includes(tag)) return;
    setEditing({ ...editing, tags: [...(editing.tags || []), tag] });
  };

  return (
    <div className="space-y-4" data-testid="notes-panel">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400">// {scopeLabel}</div>
          <div className="font-mono text-zinc-500 text-xs mt-1">{notes.length} note{notes.length === 1 ? "" : "s"}</div>
        </div>
        <div className="flex gap-2">
          <Input value={filterQ} onChange={(e) => setFilterQ(e.target.value)} placeholder="search…" className="rounded-none bg-black border-emerald-700/30 h-9 font-mono text-xs w-44" data-testid="notes-search" />
          <Input value={filterTag} onChange={(e) => setFilterTag(e.target.value)} placeholder="tag" className="rounded-none bg-black border-emerald-700/30 h-9 font-mono text-xs w-28" data-testid="notes-tag-filter" />
          <Button onClick={startNew} data-testid="notes-new-btn"
            className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono uppercase tracking-widest text-[10px] h-9">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New note
          </Button>
        </div>
      </div>

      {busy && !editing && <div className="text-center py-6 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}

      {!busy && !editing && notes.length === 0 && (
        <div className="border border-dashed border-emerald-900/40 p-10 text-center text-zinc-500 font-mono text-sm">
          No notes yet. Click <b className="text-emerald-300">New note</b> to start.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {notes.map((n) => (
          <article key={n.id} className="border border-emerald-900/30 bg-black p-4 group" data-testid={`note-${n.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-500 flex items-center gap-2">
                  <FileText className="w-3 h-3" />
                  {String(n.updated_at).slice(0, 16).replace("T", " · ")}
                  {n.source === "vasa-glean" && (
                    <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-300">
                      <Sparkles className="w-2.5 h-2.5" /> vasa-glean
                    </span>
                  )}
                </div>
                <h3 className="font-mono text-base font-bold text-emerald-100 mt-1 line-clamp-2">{n.title}</h3>
              </div>
              <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                <button onClick={() => startEdit(n)} data-testid={`note-edit-${n.id}`} className="p-1.5 border border-zinc-800 text-zinc-400 hover:text-emerald-300">
                  <FileText className="w-3 h-3" />
                </button>
                <button onClick={() => del(n.id)} data-testid={`note-delete-${n.id}`} className="p-1.5 border border-zinc-800 text-zinc-400 hover:text-red-400">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
            <div className="prose prose-invert prose-sm max-w-none font-mono text-[13px] text-zinc-300 line-clamp-5"
              dangerouslySetInnerHTML={{ __html: n.body_html }} />
            {n.tags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {n.tags.map((t) => (
                  <span key={t} onClick={() => setFilterTag(t)}
                    className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-zinc-800 text-zinc-500 hover:text-emerald-300 hover:border-emerald-700/40 cursor-pointer">
                    <TagIcon className="w-2.5 h-2.5 inline mr-1" />{t}
                  </span>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="notes-editor-modal">
          <div className="bg-black border border-emerald-700/50 max-w-3xl w-full max-h-[90vh] overflow-y-auto scrollbar-thin">
            <div className="border-b border-emerald-700/30 px-5 py-3 flex items-center justify-between">
              <div className="font-mono uppercase tracking-widest text-[11px] text-emerald-300">{editing.id ? "Edit note" : "New note"}</div>
              <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-zinc-300 font-mono text-xs">close</button>
            </div>
            <div className="p-5 space-y-4">
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Note title…" data-testid="note-title-input"
                className="rounded-none bg-black border-emerald-700/40 text-emerald-100 h-11 font-mono text-base" />
              <RichEditor value={editing.body_html} onChange={(html) => setEditing({ ...editing, body_html: html })} height={260} testid="note-rich-editor" />

              {/* Proactive Vasa — suggestions */}
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={runAutoTag} disabled={proactiveBusy} data-testid="proactive-autotag-btn"
                  className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-emerald-700/40 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-2">
                  <Wand2 className="w-3 h-3" /> Auto-tag (Vasa)
                </button>
                <button type="button" onClick={runContradictionCheck} disabled={proactiveBusy} data-testid="proactive-contradict-btn"
                  className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-amber-700/40 text-amber-300 hover:bg-amber-500/10 flex items-center gap-2">
                  <AlertTriangle className="w-3 h-3" /> Check contradictions
                </button>
                {proactiveBusy && <Loader2 className="w-3 h-3 animate-spin text-zinc-500" />}
              </div>

              {suggestedTags.length > 0 && (
                <div className="border border-emerald-700/30 bg-emerald-950/30 p-3" data-testid="proactive-tags-suggested">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 mb-2">// vasa suggests these tags</div>
                  <div className="flex flex-wrap gap-1">
                    {suggestedTags.map((t) => (
                      <button key={t} type="button" onClick={() => { addTag(t); setSuggestedTags((s) => s.filter((x) => x !== t)); }}
                        className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-emerald-700/40 text-emerald-300 hover:bg-emerald-500/20">
                        + {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {contradictions.length > 0 && (
                <div className="border border-amber-700/30 bg-amber-950/20 p-3" data-testid="proactive-contradictions">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3 h-3" /> potential contradictions ({contradictions.length})
                  </div>
                  <div className="space-y-2">
                    {contradictions.map((c, i) => (
                      <div key={i} className="text-xs text-amber-100/90 font-mono leading-relaxed border-l border-amber-700/40 pl-3">
                        <div className="text-amber-300/90 line-clamp-1"><b>vs</b> {c.past_title}</div>
                        <div><b className="text-emerald-300">new:</b> {c.new_says}</div>
                        <div><b className="text-amber-300">past:</b> {c.past_says}</div>
                        <div className="text-[10px] uppercase tracking-widest text-amber-400/60 mt-0.5">confidence · {c.confidence}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {related.length > 0 && (
                <div className="border border-zinc-800 bg-zinc-950 p-3" data-testid="proactive-related">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                    <BookOpen className="w-3 h-3" /> related past notes
                  </div>
                  <div className="space-y-1.5">
                    {related.map((r) => (
                      <div key={r.id} className="text-xs font-mono">
                        <div className="text-emerald-300 line-clamp-1">{r.title}</div>
                        <div className="text-zinc-500 line-clamp-2">{r.snippet}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="font-mono uppercase tracking-widest text-[10px] text-zinc-500 mb-2">Tags</div>
                <div className="flex flex-wrap items-center gap-2">
                  {(editing.tags || []).map((t) => (
                    <span key={t} className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-emerald-700/40 text-emerald-300 flex items-center gap-1">
                      {t}
                      <button onClick={() => setEditing({ ...editing, tags: editing.tags.filter((x) => x !== t) })} className="text-zinc-500 hover:text-red-400 ml-1">×</button>
                    </span>
                  ))}
                  <input
                    type="text"
                    placeholder="add tag + enter"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); addTag(e.target.value); e.target.value = ""; }
                    }}
                    data-testid="note-tag-input"
                    className="bg-black border border-emerald-700/30 font-mono text-xs px-2 py-1 text-emerald-200 outline-none focus:border-emerald-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="rounded-none border-emerald-700/40 bg-transparent text-emerald-300 font-mono uppercase tracking-widest text-[11px]" data-testid="note-cancel">Cancel</Button>
                <Button onClick={save} disabled={busy} data-testid="note-save"
                  className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono uppercase tracking-widest text-[11px]">
                  {busy ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-2" />}
                  {editing.id ? "Update" : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
